// hooks/useFetchTransactionsByUser.ts
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { TransactionResponse } from "../../const/transaction";
import { useAuth0 } from "@auth0/auth0-react";

const apiUrl = process.env.REACT_APP_API_URL;

interface TransactionFilter {
  id?: string;
  status?: string;
  email?: string;
  dateFrom?: string;
  dateTo?: string;
  type?: string;
}

const useFetchTransactionsByUser = (
  page: number = 1,
  pageSize: number = 10,
  filter: TransactionFilter = {}
) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const [transactions, setTransactions] = useState<TransactionResponse | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTransactions = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const tk = await getAccessTokenSilently();

      // 🔹 Construir query params dinámicos
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        ...(filter.id && { id: filter.id }),
        ...(filter.status && { status: filter.status }),
        ...(filter.email && { email: filter.email }),
        ...(filter.dateFrom && { dateFrom: filter.dateFrom }),
        ...(filter.dateTo && { dateTo: filter.dateTo }),
        ...(filter.type && { type: filter.type }),
      });

      const response = await axios.get<TransactionResponse>(
        `${apiUrl}/api/v1/transaction/search?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${tk}`,
          },
        }
      );
      setTransactions(response.data ?? null);
    } catch (err: any) {
      setError(err.message || "Error fetching transactions");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently, page, pageSize, JSON.stringify(filter)]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  return { transactions, loading, error, refetch: fetchTransactions };
};

export default useFetchTransactionsByUser;
